import { Fragment, ReactElement, ReactNode, useRef } from "react";
import {
  CellContext,
  ColumnDef,
  Row,
  RowData,
  SortDirection,
  flexRender,
  getCoreRowModel as rtGetCoreRowModel,
  getSortedRowModel as rtGetSortedRowModel,
  useReactTable,
  TableOptions,
} from "@tanstack/react-table";
import { t } from "@lingui/core/macro";
import { type css } from "styled-components";

import { Icon } from "_/components/icon";
import { TextLink } from "_/components/router";
import { Skeleton } from "_/components/skeleton";
import { useScrollOverflow } from "_/hooks/useScrollOverflow";

import { PartialBy } from "_/types";

import * as S from "./styled";

export type { ColumnDef, CellContext, Row };
export { TablePagination } from "./pagination";

// Export EmptyState for use in custom renderNoData functions
export const EmptyState = S.EmptyState;

// Extending as recommended in the react-table docs:
// https://tanstack.com/table/v8/docs/api/core/column-def#meta
declare module "@tanstack/react-table" {
  interface ColumnMeta<TData extends RowData, TValue> {
    /**
     * If true, the caller provides the <td> element. If false, the <td> is
     * provided by the `Table` component.
     **/
    customTd?: boolean;

    /**
     * Allows inserting custom CSS style overrides for this column's <td>
     * elements. Use styled-components' `css` helper to create the styles.
     * */
    customCellCss?: ReturnType<typeof css>;

    /**
     * Use "narrow" to minimize padding around columns that are intended to be skinny,
     * for example, when columns contain only icons or 1-3 letters.
     */
    variant?: "narrow" | "normal";
  }
}

type TableProps<TData> = PartialBy<TableOptions<TData>, "getCoreRowModel"> & {
  getRowProps?: (row: Row<TData>) => React.HTMLAttributes<HTMLTableRowElement>;

  /**
   * Overrides the label shown when there is no data in the table.
   * For more control over rendering, use the `renderNoData` prop instead.
   */
  noDataLabel?: string;

  /**
   * Optional render function to use for placeholder content when there is
   * no data provided to the table.
   */
  renderNoData?: ({ label }: { label?: string }) => ReactNode;

  /**
   * Renders content in a full-width row beneath each expanded data row, for
   * accordion-style detail sections. Expansion uses TanStack's expanded row
   * state: toggle a row with `row.getToggleExpandedHandler()` (from a cell or
   * via `getRowProps`) and pass `getRowCanExpand` so `row.getCanExpand()`
   * reports the right value, or control the state with `state.expanded`.
   */
  renderExpandedRow?: (row: Row<TData>) => ReactNode;

  /**
   * Set to true for tables where conserving horizontal real estate is
   * important. Can override via the `meta.variant` property in each column.
   * */
  narrowColumns?: boolean;

  /**
   * Allows overriding CSS of the <Td> component used for cells. Per-column
   * customization (which is applied on top of the table-wide `customCellCss`)
   * is also supported, via `meta.customCellCss` on each column.
   */
  customCellCss?: ReturnType<typeof css>;

  /** If true, scroll the table body instead of the entire table */
  freezeHeaders?: boolean;

  /** If true and no rows are provided, renders skeleton shimmer cells instead of the empty state */
  loading?: boolean;

  /** Number of skeleton rows to show when loading. Defaults to 4. */
  skeletonRowCount?: number;
};

const DefaultNoData = ({ label }: { label?: string }): ReactElement => {
  const defaultLabel = t`common.no-data`;
  return <S.NoData>{label ?? defaultLabel}</S.NoData>;
};

const SortIndicator = ({
  sort,
}: {
  sort: SortDirection | false;
}): ReactElement => {
  switch (sort) {
    case "asc":
      return <Icon variant="ExpandLess" />;
    case "desc":
      return <Icon variant="ExpandMore" />;
    case false:
    default:
      // Use a default icon that is hidden to preserve consistent layout.
      return <Icon style={{ visibility: "hidden" }} variant="Circle" />;
  }
};

export function Table<TData>({
  narrowColumns,
  customCellCss,
  renderNoData = DefaultNoData,
  noDataLabel,
  renderExpandedRow,
  freezeHeaders = false,
  loading = false,
  skeletonRowCount = 4,
  getCoreRowModel = rtGetCoreRowModel(),
  getSortedRowModel = rtGetSortedRowModel(),
  getRowProps,
  ...tableProps
}: TableProps<TData>): ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const overflow = useScrollOverflow(containerRef);
  const table = useReactTable({
    getCoreRowModel,
    getSortedRowModel,
    ...tableProps,
  });

  const headers = table.getHeaderGroups().map((hg) => {
    const headerCols = hg.headers.map((header) => {
      return (
        <S.Th
          key={`${header.index}_${header.id}`}
          onClick={header.column.getToggleSortingHandler()}
          colSpan={header.colSpan}
          role="columnheader"
          $variant={
            header.column.columnDef.meta?.variant ??
            (narrowColumns ? "narrow" : "normal")
          }
          $freezeHeaders={freezeHeaders}
        >
          <S.ThCell>
            {flexRender(header.column.columnDef.header, header.getContext())}
            <SortIndicator sort={header.column.getIsSorted()} />
          </S.ThCell>
        </S.Th>
      );
    });

    return (
      <S.Tr key={hg.id} role="row">
        {headerCols}
        <S.Th $freezeHeaders={freezeHeaders} />
      </S.Tr>
    );
  });

  const rows = table.getRowModel().rows.map((row) => {
    const cells = row.getVisibleCells().map((cell) => {
      const content = flexRender(cell.column.columnDef.cell, cell.getContext());

      return cell.column.columnDef?.meta?.customTd ? (
        // Custom TDs must have `role="gridcell"`. If callers derive their <td>
        // from S.Td, then they'll get this ARIA role automatically.
        <Fragment key={cell.id}>{content}</Fragment>
      ) : (
        <S.Td
          key={cell.id}
          $variant={
            cell.column.columnDef.meta?.variant ??
            (narrowColumns ? "narrow" : "normal")
          }
          $customCss={customCellCss}
          $customCssOverrides={cell.column.columnDef?.meta?.customCellCss}
        >
          {content}
        </S.Td>
      );
    });

    // The last cell in each row is a placeholder to take up all remaining space
    // without unnaturally stretching the other cells. The caller can customize
    // it, for example to change the background color or hover color.
    const gutter = (
      <S.Td
        role="gridcell"
        $variant={narrowColumns ? "narrow" : "normal"}
        $customCss={customCellCss}
      />
    );

    const expandedRow = renderExpandedRow && row.getIsExpanded() && (
      <S.ExpandedTr role="row">
        <S.FullWidthTd role="gridcell">{renderExpandedRow(row)}</S.FullWidthTd>
      </S.ExpandedTr>
    );

    const rowProps = getRowProps?.(row) ?? {};
    return (
      <Fragment key={row.id}>
        <S.Tr role="row" {...rowProps}>
          {cells}
          {gutter}
        </S.Tr>
        {expandedRow}
      </Fragment>
    );
  });

  // Visible column count required for styling rules set on the table since
  // we use display: grid for the layout.
  const columnCount = table.getLeafHeaders().length;

  const noContent = rows.length === 0 && !loading && (
    <S.Tr role="row">
      <S.FullWidthTd role="gridcell">
        {renderNoData({ label: noDataLabel })}
      </S.FullWidthTd>
    </S.Tr>
  );

  const skeletonRows =
    loading &&
    rows.length === 0 &&
    Array.from({ length: skeletonRowCount }, (_, i) => (
      <S.Tr key={`sk-${i}`} role="row">
        {Array.from({ length: columnCount }, (_, j) => (
          <S.Td
            key={j}
            role="gridcell"
            $variant={narrowColumns ? "narrow" : "normal"}
          >
            <Skeleton />
          </S.Td>
        ))}
        <S.Td role="gridcell" $variant={narrowColumns ? "narrow" : "normal"} />
      </S.Tr>
    ));

  return (
    // Don't remove this container element; it's required for Safari
    // to render grid cells with `height: 100%` correctly.
    <S.Wrapper>
      <S.ScrollShadowFrame $freezeHeaders={freezeHeaders}>
        <S.TableContainer ref={containerRef} $freezeHeaders={freezeHeaders}>
          <S.Table $columnCount={columnCount} role="grid">
            <S.Thead>{headers}</S.Thead>
            <S.Tbody>
              {rows}
              {skeletonRows}
              {noContent}
            </S.Tbody>
          </S.Table>
        </S.TableContainer>
        <S.EdgeShadow
          $side="left"
          $visible={overflow.left}
          $scrollbarWidth={overflow.scrollbarWidth}
          $scrollbarHeight={overflow.scrollbarHeight}
        />
        <S.EdgeShadow
          $side="right"
          $visible={overflow.right}
          $scrollbarWidth={overflow.scrollbarWidth}
          $scrollbarHeight={overflow.scrollbarHeight}
        />
      </S.ScrollShadowFrame>
    </S.Wrapper>
  );
}

/**
 * Render a table cell that contains a link.
 *
 * The column definition that uses this component must include `meta: {
 * customTd: true }` which will use the custom TD element contained in this
 * component instead of the default TD element used in regular cells.
 *
 * For cells that sometimes contain links and other times contain only static
 * text, then use the `<CustomCellStatic>` component to handle the static case.
 */
export const CustomCellLink = ({
  to,
  title,
  children,
}: {
  to: string;
  title?: string;
  children: ReactNode;
}) => (
  <S.CustomTd>
    <TextLink variant="default" to={to} title={title}>
      <S.CustomTdContent>{children}</S.CustomTdContent>
    </TextLink>
  </S.CustomTd>
);

/**
 * Render static text for a table cell that may sometimes contain a link.
 * This component is used in coordination with `<CustomCellLink>`.
 *
 * The column definition that uses this component must include `meta: {
 * customTd: true }` which will use the custom TD element contained in this
 * component instead of the default TD element used in regular cells.
 */
export const CustomCellStatic = ({
  children,
  title,
}: {
  children?: ReactNode;
  title?: string;
}) => (
  <S.CustomTd title={title}>
    <S.CustomTdContent>{children ?? null}</S.CustomTdContent>
  </S.CustomTd>
);
